import React, { useState, useEffect, useContext } from "react";

import { 
	Result, 
	Card, 
	InputNumber, 
	Col, 
	Row, 
	Button, 
	Drawer, 
	Space, 
	Form, 
	Input, 
	Select, 
	message,
	theme,
	Popover,	
} from "antd";

import { 
	PlusOutlined, 
	PlusCircleOutlined, 
	EditOutlined, 
	FileDoneOutlined,
	BarChartOutlined,
	MehOutlined,
	DollarOutlined
} from "@ant-design/icons";

import PropTypes from "prop-types";

import { 
	BankTransactions,
	BankAccountReport,
	BankReconciliation,
	avaliableBanks,
	formatCurrency,
} from "../components/banks";

import { CircleAnimationCompact } from "../components/loading";
import { useGetSpecificDataCompanyHook } from "../hooks";
import { get_bank_accounts, edit_bank_account } from "../services";
import { currencyGetter } from "../utils/BankCurrencyUtils";
import { BankAccountContext } from "../contexts";
import { WaitingForACompany } from "../components/WaitingForACompany";

export const BankAccounts = ({
	token, 
	company,
}) => {

	const { token: antToken } = theme.useToken();	

	const { 
		data : bankAccounts = [],
		backupData: backupBankAccounts, 
		loading,
		addNewData,
		updateData,
	} = useGetSpecificDataCompanyHook(company, get_bank_accounts, token, edit_bank_account, {
		aditionalParamsForFetch: {
			type_search: "all",
			extra: {},
		}
	});

	const [ openCreate, setOpenCreate ] = useState(false);
	const [ form ] = Form.useForm();
	const [ submittable, setSubmittable ] = useState(false);
	const { bankAccountSelected, setBankAccountSelected, calculateUpdatedBalance } = useContext(BankAccountContext);
	const [ bankAccountReportSelected, setBankAccountReportSelected ] = useState(null);
	const [ bankAccountForReconciliation, setbankAccountForReconciliation ] = useState(null);
	const [ bankAccountToEdit, setBankAccountToEdit ] = useState(null);
	const [ loadingEditBankAccount, setLoadingEditBankAccount ] = useState(false);

	useEffect(() => {
		form.validateFields({
			validateOnly: true,
		}).then(() => setSubmittable(true))
			.catch(() => setSubmittable(false));
	}, [openCreate, form, Form.useWatch([], form)]);

	useEffect(() => {
		if (bankAccountToEdit) {
			form.setFieldsValue({
				...bankAccountToEdit,
				diary: bankAccountToEdit.diaries?.id || undefined,
			});
		}
	}, [bankAccountToEdit, form]);

	const createNewBankAccount = async () => {
		try {
			await form.validateFields();
		} catch (e) {
			message.error(`Validacion Fallida: ${e}`);
			return;
		}
		
		const bank = form.getFieldValue("bank");
		const numberAccount = form.getFieldValue("account_number");
		const currency = form.getFieldValue("currency");
		const diary_id = form.getFieldValue("diary");
		const initial_amount = form.getFieldValue("initial_amount");
		
		const foundBankAccount = backupBankAccounts.find(
			bankAccount => bankAccount.bank === bank && bankAccount.account_number === numberAccount && bankAccount.currency === currency
		);

		if(foundBankAccount){
			message.error("Ya existe una cuenta bancaria con estos datos");
			return;
		}

		const diaries = diary_id ? company.accounting_resources?.diaries.find(diary => diary.id === diary_id) : {};
		
		const dataBankAccount = {
			bank,
			account_number: numberAccount,
			currency,
			company_id: company.id,
			diaries : diaries ? diaries : {},
			initial_amount
		};

		const result = await addNewData(dataBankAccount, {balance: initial_amount});
		if(result){			
			setOpenCreate(false);
			form.resetFields();			
			message.success("Se creo de manera exitosa la nueva cuenta bancaria.");
		}
	};

	const updateBankAccount = async () => {
		setLoadingEditBankAccount(true);

		try {			
			const { 
				account_number,
				bank,
				currency,
				diary: diaryToSet = null,
				initial_amount,
			} = form.getFieldsValue();
						
			const updatedBankAccount = {
				id: bankAccountToEdit.id,
				account_number,
				bank,
				currency,
				initial_amount,
				diaries: company.accounting_resources?.diaries.find(diary => diary.id === diaryToSet) || {},
			};
						
			const response = await edit_bank_account(token, "edit", updatedBankAccount);
			if (response) {
				const { message: messageGetted, basePoints = {}, temporalBasePoints = {} } = response;
				
				message.success(messageGetted);

				// ! OTHER METHOD FOR UPDATE EXCLUSEVILY THIS DATA.
				const { balanceToUpdate } = calculateUpdatedBalance({
					initialAmount: initial_amount,
					basePoints,
					temporalBasePoints,
					bankAccount: bankAccountToEdit,
				});

				updateData({
					id: bankAccountToEdit.id, 
					basePoints, 
					temporalBasePoints, 
					initial_amount: updatedBankAccount.initial_amount,
					balance: balanceToUpdate,
					bank: updatedBankAccount.bank,
					account_number: updatedBankAccount.account_number,
					currency: updatedBankAccount.currency,
					diaries: updatedBankAccount.diaries,
				});
				
				setBankAccountToEdit(null);
				form.resetFields();
			} else {
				message.error("Error al actualizar la cuenta bancaria.");
			}            
		} catch (validationError) {
			console.error("Validation Error:", validationError);
			message.error("Por favor completa todos los campos requeridos.");
		} finally {
			setLoadingEditBankAccount(false);
		}
	};
	
	const BankAccountsView = (<>			
		{/*ADD BUTTON FOR CREATE A NEW BANK ACCOUNT*/}
		<Button
			type="primary"
			size="large"
			icon={<PlusOutlined />}
			style={{
				marginBottom: "40px",
				width: "100%",
				height: "50px",
				fontSize: "20px",
			}}
			onClick={() => {
				form.resetFields();
				setOpenCreate(true);
			}}
		>
			Agregar Nueva Cuenta Bancaria
		</Button>

		{
			bankAccounts.length === 0 && <Result
				icon={<MehOutlined />}
				title="Esta Vacío, no hay cuentas bancarias"
			/>
		}

		{/*ROW FOR SHOW THE BANK ACCOUNTS*/}
		<Row gutter={16}>
			{bankAccounts.map((item, index) => (
				<Col 
					key={index} 
					span={6}									
				>
					<Card
						title={`Cuenta ${item.account_number} Banco ${item.bank}`} 
						bordered={true} 
						actions={[
							<Popover placement="bottomLeft" title="Transacciones" trigger="hover" key="transactions">
								<FileDoneOutlined onClick={() => { 
									setBankAccountSelected(item); 
								}}/>
							</Popover>,
							<Popover placement="bottom" title="Reporte de la Cuenta Bancaria" trigger="hover" key="reports">
								<BarChartOutlined onClick={() => {
									setBankAccountReportSelected(item);
								}}/>
							</Popover>,
							<Popover placement="bottom" title="Reconciliación Bancaria" trigger="hover" key="reconciliation">
								<DollarOutlined onClick={() => {
									setbankAccountForReconciliation(item);
								}}/>
							</Popover>,
							<Popover placement="bottomRight" title="Editar Cuenta Bancaria" trigger="hover" key="edit">
								<EditOutlined onClick={() => {
									form.resetFields();
									setBankAccountToEdit(item);
								}}/>
							</Popover>,														
						]}
						style={{
							marginBottom: "25px", 
							marginTop: "10px",
							borderColor: antToken.colorBorderSecondary,
							borderWidth: "2px",
							borderStyle: "solid",
						}}
					>
						<p style={{marginBottom: "5px", marginTop: "-10px", fontSize: "18px"}}>{item.currency}</p>							
						<p style={{marginBottom: "-10px", marginTop: "0px", fontSize: "18px"}}>{currencyGetter[item.currency]} {formatCurrency(item.balance)}</p>
					</Card>
				</Col>
			))}
		</Row>

		{/*DRAWER FOR SHOW THE PANEL FOR ADD A NEW BANK ACCOUNT*/}
		<Drawer
			title="Agregar Nueva Cuenta Bancaria"
			placement="right"
			width={"40vw"}
			open={openCreate}
			onClose={() => {
				setOpenCreate(false);
				form.resetFields();
			}}
			extra={
				<Space>
					<Button 
						type="primary" 
						icon={<PlusCircleOutlined />} 
						disabled={!submittable}
						onClick={createNewBankAccount}
					>
						Crear
					</Button>
				</Space>
			}
		>
			<Form form={form} name="validateOnly" layout="horizontal" autoComplete="off">
				<Form.Item 
					name="bank"
					label="Banco"
					rules={[
						{
							required: true,
						},
					]}
				>
					<Select>
						{avaliableBanks.map(({value, label}) => (
							<Select.Option key={value} value={value}>{label}</Select.Option>
						))}
					</Select>
				</Form.Item>

				<Form.Item
					name="account_number"
					label="Número de Cuenta"
					rules={[
						{
							required: true,
						},
					]}
				>
					<Input />
				</Form.Item>

				<Form.Item 
					name="currency"
					label="Moneda"
					rules={[
						{
							required: true,
						},
					]}
				>
					<Select>
						<Select.Option value="GTQ">GTQ</Select.Option>
						<Select.Option value="USD">USD</Select.Option>
					</Select>
				</Form.Item>

				<Form.Item 
					name="initial_amount"
					label="Monto Inicial"
					rules={[
						{
							required: true,
						},
					]}
				>
					<InputNumber
						style={{
							width: "100%",
							borderRadius: 5
						}}
						min={0.00}
						precision={2}
						step={0.01}
						controls={false}
					/>
				</Form.Item>

				<Form.Item 
					name="diary"
					label="Diario"
				>
					<Select>
						{company.accounting_resources?.diaries?.map((diary_item) => (
							<Select.Option key={diary_item.id} value={diary_item.id}>
								{diary_item.name}
							</Select.Option>
						))}
					</Select>
				</Form.Item>				
			</Form>
		</Drawer>

		{/*DRAWER FOR SEE THE TRANSACTIONS FROM A SPECIFIC BANK ACCOUNT*/}
		{
			bankAccountSelected && 
			<BankTransactions
				token={token}				
				company={company}
				updateData={updateData}
			/>
		}

		{/*DRAWER FOR SEE THE REPORTS FOR THE TRANSACTIONS*/}
		{
			bankAccountReportSelected &&
			<BankAccountReport
				bankAccountReportSelected={bankAccountReportSelected}
				setBankAccountReportSelected={setBankAccountReportSelected}
				token={token}
				company={company}
			/>
		}

		{/*DRAWER FOR BANK RECONCILIATION*/}
		{
			bankAccountForReconciliation && <BankReconciliation			
				// Escential Properties
				token={token}
				bankAccountSelected={bankAccountForReconciliation}
				companySelected={company}
				// Functions
				onBankAccountSelected={setbankAccountForReconciliation}
			/>
		}

		{/*DRAWER FOR SHOW THE PANEL FOR EDIT A BANK ACCOUNT*/}
		<Drawer
			title={`Editar Cuenta Bancaria ${bankAccountToEdit?.account_number || ""}`}
			placement="right"
			width={"50vw"}
			open={bankAccountToEdit !== null}
			onClose={() => {
				setBankAccountToEdit(null);
				form.resetFields();
			}}
			loading={loadingEditBankAccount}
			extra={
				<Space>
					<Button 
						type="primary" 
						icon={<PlusCircleOutlined />} 
						disabled={!submittable}
						onClick={updateBankAccount}
						loading={loadingEditBankAccount}
					>
						Guardar Cambios
					</Button>
				</Space>
			}
		>
			<Form 
				form={form} 
				name="editBankAccount" 
				layout="horizontal" 
				autoComplete="off" 
				initialValues={bankAccountToEdit}
			>
				<Form.Item 
					name="bank"
					label="Banco"
					rules={[
						{
							required: true,
							message: "Por favor selecciona un banco",
						},
					]}
				>
					<Select>
						{avaliableBanks.map(({value, label}) => (
							<Select.Option key={value} value={value}>{label}</Select.Option>
						))}
					</Select>
				</Form.Item>

				<Form.Item
					name="account_number"
					label="Número de Cuenta"
					rules={[
						{
							required: true,
							message: "Por favor ingresa el número de cuenta",
						},
					]}
				>
					<Input />
				</Form.Item>

				<Form.Item 
					name="currency"
					label="Moneda"
					rules={[
						{
							required: true,
							message: "Por favor selecciona una moneda",
						},
					]}
				>
					<Select>
						<Select.Option value="GTQ">GTQ</Select.Option>
						<Select.Option value="USD">USD</Select.Option>
					</Select>
				</Form.Item>

				<Form.Item 
					name="initial_amount"
					label="Monto Inicial"
					rules={[
						{
							required: true,
							message: "Por favor ingresa el monto inicial",
						},
						{
							type: "number",
							min: 0,
							message: "El monto inicial debe ser un número mayor o igual a 0",
						},
					]}
				>
					<InputNumber
						style={{
							width: "100%",
							borderRadius: 5,
						}}
						min={0.00}
						precision={2}
						step={0.01}
						controls={false}
					/>
				</Form.Item>

				<Form.Item 
					name="diary"
					label="Diario"
				>
					<Select>
						{company.accounting_resources?.diaries?.map((diary_item) => (
							<Select.Option key={diary_item.id} value={diary_item.id}>
								{diary_item.name}
							</Select.Option>
						))}
					</Select>
				</Form.Item>
			</Form>
		</Drawer>
	</>);
	
	return (
		<>
			{
				loading && <CircleAnimationCompact/>
			}
			{
				!loading && <>
					<WaitingForACompany company_id={company.id}/>
					{company.id !== "" && BankAccountsView}
				</>			
			}
		</>
	);
};

BankAccounts.propTypes = {
	token: PropTypes.string.isRequired,
	company: PropTypes.shape({
		id: PropTypes.string.isRequired,
		tid: PropTypes.string.isRequired,
		name: PropTypes.string.isRequired,
		accounting_resources: PropTypes.object.isRequired,
	}).isRequired,
};